import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { enablePush, pushSupported, permission, isIOS, isStandalone } from "@/lib/push";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Smartphone, Share, PlusSquare, MoreVertical, Download, Bell, Loader2, Compass } from "lucide-react";
import { toast } from "sonner";
import { tx } from "@/i18n";

const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
const isAndroid = /android/i.test(ua);
const isMobile = isIOS || isAndroid || /mobile/i.test(ua);
const iosNotSafari = isIOS && /crios|fxios|edgios|opios|gsa\//i.test(ua);

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3">
    <span className="grid place-items-center h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">{n}</span>
    <span className="text-sm flex items-center gap-1.5 flex-wrap">{children}{Icon && <Icon className="h-4 w-4 text-muted-foreground" />}</span>
  </li>
);

const InstallStep = ({ prompt, onInstall, onNext }) => (
  <div className="space-y-4" data-testid="onboarding-install-step">
    {isIOS ? (
      iosNotSafari ? (
        <p className="text-sm flex gap-2" data-testid="onboarding-ios-open-safari"><Compass className="h-4 w-4 shrink-0 mt-0.5 text-primary" /> {tx("Open deze pagina in Safari: alleen via Safari kun je de app op je beginscherm zetten.")}</p>
      ) : (
        <ol className="space-y-2.5" data-testid="onboarding-ios-steps">
          <Step n="1" icon={Share}>{tx("Tik onderin op de Deel-knop")}</Step>
          <Step n="2" icon={PlusSquare}>{tx("Kies 'Zet op beginscherm'")}</Step>
          <Step n="3">{tx("Tik op 'Voeg toe' en open Costa Key vanaf je beginscherm")}</Step>
        </ol>
      )
    ) : prompt ? (
      <Button onClick={onInstall} className="w-full bg-primary hover:bg-primary/90 gap-2" data-testid="onboarding-install-btn">
        <Download className="h-4 w-4" /> {tx("App installeren")}
      </Button>
    ) : (
      <ol className="space-y-2.5" data-testid="onboarding-android-steps">
        <Step n="1" icon={MoreVertical}>{tx("Tik rechtsboven in Chrome op het menu")}</Step>
        <Step n="2" icon={Download}>{tx("Kies 'App installeren' of 'Toevoegen aan startscherm'")}</Step>
        <Step n="3">{tx("Bevestig: het icoon verschijnt op je startscherm")}</Step>
      </ol>
    )}
    <Button variant="ghost" onClick={onNext} className="w-full" data-testid="onboarding-install-skip">
      {prompt && !isIOS ? tx("Overslaan") : tx("Volgende")}
    </Button>
  </div>
);

const PushStep = ({ onDone }) => {
  const [busy, setBusy] = useState(false);
  const turnOn = async () => {
    setBusy(true);
    try {
      await enablePush();
      window.dispatchEvent(new Event("push-enabled"));
      toast.success(tx("Meldingen staan aan op dit apparaat"));
    } catch (e) {
      toast.error(e.message === "denied" ? tx("Meldingen zijn geblokkeerd. Sta ze toe in de instellingen van je telefoon.") : tx("Meldingen aanzetten is niet gelukt"));
    } finally { setBusy(false); onDone(); }
  };
  return (
    <div className="space-y-3" data-testid="onboarding-push-step">
      <Button onClick={turnOn} disabled={busy} className="w-full bg-primary hover:bg-primary/90 gap-2" data-testid="onboarding-push-enable">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />} {tx("Meldingen aanzetten")}
      </Button>
      <Button variant="ghost" onClick={onDone} className="w-full" data-testid="onboarding-push-later">{tx("Later")}</Button>
    </div>
  );
};

export const MobileOnboarding = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(null);
  const [prompt, setPrompt] = useState(() => window.__installPrompt || null);
  const ob = user?.onboarding || {};
  const canPush = pushSupported() && permission() === "default";

  useEffect(() => {
    if (user && isStandalone() && !sessionStorage.getItem("ck_app_opened")) {
      sessionStorage.setItem("ck_app_opened", "1");
      api.post("/auth/me/app-opened").catch(() => {});
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isMobile || !user) return;
    if (!ob.install && !isStandalone()) setStep("install");
    else if (!ob.push && canPush) setStep("push");
    const ready = () => setPrompt(window.__installPrompt);
    window.addEventListener("installprompt-ready", ready);
    return () => window.removeEventListener("installprompt-ready", ready);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const mark = (s) => api.post("/auth/me/onboarding", { step: s }).catch(() => {});
  const nextFromInstall = () => {
    mark("install");
    // iOS only allows push from the home-screen app; ask there on first open.
    setStep(!ob.push && canPush ? "push" : null);
  };
  const install = async () => {
    prompt.prompt();
    await prompt.userChoice.catch(() => null);
    window.__installPrompt = null;
    setPrompt(null);
    nextFromInstall();
  };
  const finishPush = () => { mark("push"); setStep(null); };
  const close = () => (step === "install" ? nextFromInstall() : finishPush());

  return (
    <Dialog open={!!step} onOpenChange={(o) => !o && close()}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl" data-testid="mobile-onboarding-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {step === "push" ? <Bell className="h-5 w-5 text-primary" /> : <Smartphone className="h-5 w-5 text-primary" />}
            {step === "push" ? tx("Stap 2 · Meldingen aanzetten") : tx("Stap 1 · Zet Costa Key op je telefoon")}
          </DialogTitle>
          <DialogDescription>
            {step === "push"
              ? tx("Krijg direct een melding bij nieuwe chatberichten, afspraken en opmerkingen.")
              : tx("Open de portal voortaan met één tik vanaf je startscherm, zonder adresbalk.")}
          </DialogDescription>
        </DialogHeader>
        {step === "install" && <InstallStep prompt={prompt} onInstall={install} onNext={nextFromInstall} />}
        {step === "push" && <PushStep onDone={finishPush} />}
      </DialogContent>
    </Dialog>
  );
};
